import React, { useMemo } from 'react'

import type { Product } from 'types'

import { Stack, Typography, useTheme } from '@mui/material'

// Direct file imports, NOT the `components` barrel — this file is part of that barrel, so
// importing back through it is a cycle (same note as `ActionLine` / `ScanTargetButton`).
import { SearchFilter } from '../filters/SearchFilter'
import { ProductPreview } from '../../display/Preview/ProductPreview/ProductPreview'

interface ProductSelectFieldProps {
  /** The company's products. Fetch upstream so the caller controls scoping (company_id, archived). */
  products: Product[]
  value: Product | null
  onChange: (product: Product | null) => void
  /**
   * A `tag_shop_product` id. Products whose DEFAULT TAG is this tag sort first and carry a
   * `Default tag` badge. A HINT ONLY — every product stays selectable, because the default-tag
   * relationship is advisory and a brand may legitimately put a different tag on a product.
   */
  defaultForTagId?: string | null
  /** Field label above the search box / selected preview. */
  label?: string
  /** Show the current pick but refuse to change it (e.g. the binding is frozen server-side). */
  readOnly?: boolean
}

/**
 * VRG-282 — pick ONE of the company's products.
 *
 * Built on the shared `SearchFilter` in its `notFilter singleSelect` picker mode — the same
 * primitive `SetDefaultTagModal` already uses for the inverse choice (pick a TAG for a product).
 * That was chosen over the two other candidates deliberately:
 *
 *  - `SelectField` / `Select` are `{label, value}` STRING options. A product row is an image +
 *    title + SKU, and forcing that through a string label would lose the preview the whole
 *    picker is for.
 *  - `MenuButton` (@repo/ui) is a button-anchored action menu, not a searchable option list;
 *    a company can easily have hundreds of products, so search is not optional.
 *
 * `SearchFilter` gives the labelled field, the type-to-filter search, the anchored popover, and
 * `SearchSelect`'s `preview: 'products'` rows — i.e. the existing `ProductPreview` presentation,
 * not a new one.
 */
export const ProductSelectField = (props: ProductSelectFieldProps) => {
  const {
    products,
    value,
    onChange,
    defaultForTagId,
    label = 'Product',
    readOnly = false,
  } = props
  const theme = useTheme()

  // `default_tag_id`, NOT a nested `tag_shop_product`. A BRAND product records its default tag as
  // a flat id (`products.default_tag_id`, the tag SKU's Shopify product id — see
  // `SetDefaultTagModal`); only a PACKAGE carries a `tag_shop_product` object. Reading the nested
  // shape here compared `undefined` against the id on every row, so the comparison was always
  // false: no badge ever rendered and the ordering never moved anything, silently. Caught by
  // setting a real default and seeing the list not change.
  //
  // Compared as strings because the two sides arrive differently typed — the package's tag id can
  // come back numeric while `default_tag_id` is a string column.
  const isDefaultTagMatch = (product: any) =>
    !!defaultForTagId && String(product?.default_tag_id ?? '') === String(defaultForTagId)

  /**
   * Default-tag matches first, everything else after, each group keeping the order the server
   * sent. `Array.prototype.sort` is required to be stable (ES2019+), so a comparator that only
   * distinguishes the two groups is a partition — no secondary sort key needed, and no risk of
   * reshuffling products that are otherwise equally ranked.
   */
  const orderedProducts = useMemo(() => {
    if (!defaultForTagId) return products
    return [...products].sort(
      (a, b) => Number(isDefaultTagMatch(b)) - Number(isDefaultTagMatch(a)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, defaultForTagId])

  // A filled zinc chip, not a coloured one: this marks the RECOMMENDED rows, and a loud badge on
  // several rows at once would read as a status the others are failing. The fill is `primary[100]`
  // — the same neutral step the table and every control border use — so it reads as a quiet label
  // rather than a state.
  //
  // Squarer than the surrounding controls on purpose. Those are 8px and interactive; this is a
  // static label sitting inside one of them, and matching their radius made it look like a nested
  // button. A smaller radius nests cleanly, the same reasoning the dropdown's own rows use.
  const renderDefaultTagBadge = (item: any) =>
    isDefaultTagMatch(item) ? (
      <Typography
        variant="body4"
        color="text.secondary"
        sx={{
          px: 3,
          py: 1.5,
          borderRadius: 1,
          whiteSpace: 'nowrap',
          backgroundColor: theme.palette.primary[100],
        }}
      >
        Default tag
      </Typography>
    ) : null

  if (value) {
    return (
      <Stack spacing={4}>
        <Typography variant="body2">{label}</Typography>
        {/* The "Change" affordance lives in the shared `Preview` (onChange) — clearing the pick
            re-opens the search. Omitted when read-only so a frozen binding has no control that
            looks live. */}
        <ProductPreview
          product={value}
          onChange={readOnly ? undefined : () => onChange(null)}
        />
      </Stack>
    )
  }

  return (
    <SearchFilter
      notFilter
      singleSelect
      label={label}
      column={{ headerName: label, field: 'product' } as any}
      // The `product` field name would otherwise pick up a scan target (SCAN_ENTITY_BY_FIELD).
      // Off here on purpose: this is a CATALOG choice, and arming the reader from a checkout
      // page or an assign modal would silently redirect the operator's next tag scan.
      showScan={false}
      rows={orderedProducts as any}
      filter={[] as any}
      searchParams={{ fields: ['title', 'sku'], preview: 'products' }}
      listItemSecondaryAction={renderDefaultTagBadge}
      // Only a real catalog row is a pick. `SearchFilter`'s `onChange` is shape-polymorphic —
      // filter mode emits `{field, operator, value}`, picker mode emits the item — so a stray
      // descriptor arriving here would be stored as the "selected product" and render as a blank
      // card. Guarding on `id` keeps that shape from ever reaching `ProductPreview` again, at the
      // one boundary where the two contracts meet.
      onChange={((item: any) => onChange(item?.id ? item : null)) as any}
    />
  )
}
